<script setup lang="ts">
import { onErrorCaptured, onBeforeMount, type Ref, ref, computed, watch, defineAsyncComponent, shallowRef } from 'vue';
import { useLocale } from 'vuetify';
import { useI18n } from 'vue-i18n';
import '@fortawesome/fontawesome-svg-core/styles.css';

import {
  AssessmentEngine,
  PersonalityAssessmentEngine,
  ReportEngine,
  ReportBlockedError,
  ServerError,
  ChildAssessmentEngine,
} from '@/lib/sdk';
import type { Configuration, ValidatedConfiguration } from '@/ClientConfiguration';
import { deprecatedConfigurationIds } from '@/ConfigurationValidation';
import { useAssessmentInit, useConfigurationInit } from '@/use/useAssessmentInit';
import * as languages from '@/i18n/Languages';
import { AssessmentEngineLogicError } from '@/errors/assessment-engine-logic-error';
import { CareerProfileMissingDataError } from '@/errors/career-profile-missing-data-error';
import { DeprecatedConfigurationStartError } from '@/errors/deprecated-configuration-start-error';
import { logError } from '@/use/useSharedScripts';
import { IncompleteAssessmentReadOnlyError } from '@/errors/incomplete-assessment-readonly-error';
import { ReportProps } from '@/ReportProps';
import { type EngineType } from '@/lib/sdk';
import ProgressSpinner from '@/components/ProgressSpinner.vue';

const props = defineProps<{
  unvalidatedconfiguration: Configuration;
}>();

declare global {
  interface Window {
    dummyHesAssessmentClientConfiguration: Configuration;
    startHesAssessmentClient: (unvalidatedconfiguration: Configuration) => void;
    loadHesAssessment: (unvalidatedconfiguration: Configuration) => void;
  }
}

// Async work while moving between pages: the page being left stays on screen until the next one is
// ready, so it is covered by a loading overlay. The tracked spans run one after another today (a
// calculation, then loading the report). It is a count so that overlapping spans (displayCurrentState
// running twice, or the host calling loadHesAssessment mid-load) cannot clear the overlay while work
// is still running. The overlay clears on failure as well as success.
const activeTransitionCount = ref(0);
const isTransitioning = computed((): boolean => activeTransitionCount.value > 0);
const trackTransition = async (work: () => Promise<void>): Promise<void> => {
  activeTransitionCount.value += 1;

  try {
    await work();
  } finally {
    activeTransitionCount.value -= 1;
  }
};

// eslint-disable-next-line id-length
const { t, locale } = useI18n(),
  { current } = useLocale(),
  assessmentEngine: Ref<AssessmentEngine | PersonalityAssessmentEngine | ChildAssessmentEngine | null> = ref(null),
  reportEngine: Ref<ReportEngine | null> = ref(null),
  reportProps: Ref<ReportProps | null> = ref(null),
  configuration: Ref<ValidatedConfiguration> = ref({}) as Ref<ValidatedConfiguration>,
  page = ref('Loading'),
  nextPage: Ref<string | null> = ref(null),
  component = shallowRef(defineAsyncComponent(() => import(`@/components/${page.value}.vue`))),
  message = ref(''),
  isReportBlocked = ref(false),
  zero = 0,
  renderCount = ref(zero),
  productSuiteClass: Ref<string | undefined> = ref(''),
  isResuming = ref(false),
  isError = ref(false),
  errorMessage = ref(''),
  errorType = ref(''),
  resetError = (): void => {
    isError.value = false;
    errorMessage.value = '';
    errorType.value = '';
  },
  handleError = (error: unknown): void => {
    if (error instanceof Error) {
      logError(error, props.unvalidatedconfiguration);

      message.value = error.message;

      if (error instanceof ReportBlockedError) {
        message.value = `${t('Your report is currently locked. In order to view your report, you will need to contact the person who instructed you to do the assessment, or another person at your site who can unlock the report. Reviewing the report with a qualified individual, who can answer questions and explain things clearly, will provide the most benefit to you.')}`;
        isReportBlocked.value = true;
      } else if (error instanceof ServerError && error.httpStatusCode === 401) {
        message.value = `${t('Please reload the page and try again')}`;
      } else if (error instanceof IncompleteAssessmentReadOnlyError) {
        message.value = `${t('Assessment incomplete. You cannot view an incomplete assessment.')}`;
      }

      nextPage.value = 'Error';
    }

    throw error;
  },
  setLocale = (newLocale: string): void => {
    if (newLocale && languages.getLanguage(newLocale)) {
      if (newLocale !== current.value) {
        locale.value = newLocale;
        current.value = newLocale;
      }
    } else {
      handleError(new Error('locale does not exist'));
    }
  },
  setReportProperties = async (engine: EngineType) => {
    if (engine instanceof ReportEngine) {
      reportEngine.value = engine;
    } else {
      reportEngine.value = await engine.getReportEngine(configuration.value.enableReportBlocking);
    }

    reportProps.value = await ReportProps.getReportProps(
      reportEngine.value.getReport().elements[0],
      reportEngine.value,
      configuration.value,
    );
    productSuiteClass.value = reportEngine.value.getProductConfiguration().productSuite;
  },
  reloadPage = async () => {
    let locale = assessmentEngine.value === null ? configuration.value.locale : assessmentEngine.value.getLocale();

    if (page.value === 'Report' && reportEngine.value === null) {
      if (assessmentEngine.value === null) {
        handleError(new Error('The assessment engine did not initialize'));
      } else {
        try {
          await setReportProperties(assessmentEngine.value);
        } catch (error) {
          handleError(error);
        }
      }
    }

    locale = reportEngine.value === null ? locale : reportEngine.value.getLocale();
    setLocale(locale);
    renderCount.value += 1;
  },
  startChildAssessment = async (assessmentData: { configurationId: string; assessmentId?: string }): Promise<void> => {
    if (reportEngine.value == null) {
      handleError(new Error('The report engine did not initialize'));
    }

    const childConfiguration = { ...configuration.value } as Configuration;

    delete childConfiguration.assessmentId;

    if (assessmentData.assessmentId) {
      childConfiguration.assessmentId = assessmentData.assessmentId;
    }

    childConfiguration.configurationId = assessmentData.configurationId;
    childConfiguration.parentAssessmentId = reportEngine.value.getAssessmentId();

    await loadHesAssessment(childConfiguration);

    return;
  },
  // eslint-disable-next-line id-length
  displayCurrentState = async (fn: string): Promise<void> => {
    if (fn === 'reloadPage') {
      reloadPage();

      return;
    }

    if (assessmentEngine.value !== null) {
      switch (assessmentEngine.value.getStatus()) {
        case AssessmentEngine.STATE_ANSWER_QUESTIONS: {
          if (assessmentEngine.value instanceof ChildAssessmentEngine) {
            if (ChildAssessmentEngine.PAGE_DISPLAY_TYPE_MEMORY === assessmentEngine.value.getPageDisplayType()) {
              nextPage.value = 'MemoryQuestion';
              break;
            } else {
              nextPage.value = 'MultipleQuestions';
              break;
            }
          }

          nextPage.value = 'Question';
          break;
        }

        case PersonalityAssessmentEngine.STATE_RATE_CAREER_CLUSTERS: {
          nextPage.value = 'RateCareerClusters';
          break;
        }

        case AssessmentEngine.STATE_CALCULATE_ASSESSMENT: {
          await trackTransition(async () => {
            if (assessmentEngine.value instanceof ChildAssessmentEngine) {
              await assessmentEngine.value.stopTimer();
            }

            await assessmentEngine.value.calculateAssessment();
          });

          if (assessmentEngine.value instanceof PersonalityAssessmentEngine) {
            nextPage.value = null;
            await displayCurrentState('displayCurrentState');
          } else {
            nextPage.value = 'Loading';
          }

          break;
        }

        case PersonalityAssessmentEngine.STATE_TIE_RESOLVEMENT: {
          nextPage.value = 'Question';
          break;
        }

        case PersonalityAssessmentEngine.STATE_RATE_ASSESSMENT: {
          nextPage.value = 'RateAssessment';
          break;
        }

        case AssessmentEngine.STATE_REPORT: {
          nextPage.value = 'Loading';
          break;
        }

        default: {
          throw new AssessmentEngineLogicError('Invalid state');
        }
      }
    }
  },
  setCSSCustomProperties = () => {
    if (configuration.value.customColors) {
      const root: HTMLDivElement | null = document.getElementsByClassName('hesAPISPA')[0] as HTMLDivElement;

      if (root) {
        for (const [key, value] of Object.entries(configuration.value.customColors)) {
          root.style.setProperty(`--hes-color-${key}`, value);
        }
      }
    }
  };

if (props.unvalidatedconfiguration.locale) {
  setLocale(props.unvalidatedconfiguration.locale);
}

onErrorCaptured((error: Error): boolean | void => {
  isError.value = true;
  errorMessage.value = error.message;
  errorType.value = error.name;

  // Raw internals (assessment/career ids) - never shown to the user; logError still gets the
  // original message below.
  if (error instanceof CareerProfileMissingDataError) {
    errorMessage.value = `${t('We were unable to load your compare-to-career match data.')}`;
  }

  logError(error, props.unvalidatedconfiguration);

  return false;
});

const loadHesAssessment = async (unvalidatedconfiguration: Configuration): Promise<void> => {
  try {
    if (
      !props.unvalidatedconfiguration.assessmentId &&
      deprecatedConfigurationIds.includes(props.unvalidatedconfiguration.configurationId)
    ) {
      throw new DeprecatedConfigurationStartError(`${t('This version of Skills cannot be started.  Please use the new configuration.')}`);
    }

    // eslint-disable-next-line require-atomic-updates
    configuration.value = await useConfigurationInit(import.meta.env.VITE_TEST_ENVIRONMENT, unvalidatedconfiguration);

    const engine = await useAssessmentInit(configuration.value),
      thisWindow = window as any;

    if (thisWindow.Cypress) {
      thisWindow.assessmentId = engine.getAssessmentId();
      thisWindow.userId = engine.getUser().id;
    }

    setLocale(configuration.value.locale as string);

    if (engine instanceof ReportEngine) {
      await setReportProperties(engine);
      nextPage.value = 'Loading';
    } else {
      // instructors cannot view incomplete assessments
      if (configuration.value.readOnly === true) {
        throw new IncompleteAssessmentReadOnlyError();
      }

      assessmentEngine.value = engine;
      reportEngine.value = null;

      if (configuration.value.onAssessmentStart && !configuration.value.assessmentId) {
        configuration.value.onAssessmentStart(assessmentEngine.value.getAssessment());
      }

      isResuming.value = engine instanceof ChildAssessmentEngine && !!engine.getAssessment().timer;

      if (isResuming.value) {
        await displayCurrentState('displayCurrentState');
      } else {
        nextPage.value = 'IntroText';
      }

      productSuiteClass.value = assessmentEngine.value.getProductConfiguration().productSuite;
    }

    setCSSCustomProperties();
  } catch (error) {
    handleError(error);
  }
};

onBeforeMount(async () => {
  await loadHesAssessment(props.unvalidatedconfiguration);
});

watch(
  nextPage,
  async () => {
    try {
      if (nextPage.value) {
        // Next page is a report
        if (nextPage.value === 'Loading') {
          // The page being left (unless it is the Loading page, which has its own spinner) stays
          // on screen while the report loads.
          const isLeavingPage = page.value !== 'Loading';

          page.value = nextPage.value;

          // If report is accessed after completing the assessment
          if (assessmentEngine.value !== null) {
            // eslint-disable-next-line max-depth
            if (configuration.value.onAssessmentComplete) {
              configuration.value.onAssessmentComplete(assessmentEngine.value.getAssessment());
            }

            // Initialize the report engine
            // eslint-disable-next-line max-depth
            if (isLeavingPage) {
              await trackTransition(() => setReportProperties(assessmentEngine.value));
            } else {
              await setReportProperties(assessmentEngine.value);
            }
          }

          if (!reportEngine.value) {
            throw new Error('The report engine did not initialize');
          }

          page.value = reportEngine.value.getStatus();
          component.value = defineAsyncComponent(() => import(`./components/${page.value}.vue`));
        } else {
          page.value = nextPage.value;
          component.value = defineAsyncComponent(() => import(`./components/${page.value}.vue`));
        }
      }
    } catch (error) {
      handleError(error);
    }
  },
  { flush: 'sync' },
);

window.loadHesAssessment = loadHesAssessment;
</script>

<template>
  <v-app :id="configuration.applicationDivId" :class="productSuiteClass" data-cy="app-wrapper">
    <v-alert :model-value="isError" type="error" elevation="5" class="hes-alert" :title="t('ERROR')" @click="resetError()">
      <template #text>
        <p>{{ t('The following error has occurred') }} :</p>
        <p>
          <b>{{ errorType }}</b>
          <br />
          {{ errorMessage }}
        </p>
        <p>
          {{ t('Please reload the page and try again.') }}
        </p>
        <p>
          {{ t('If the error continues, contact your system administrator.') }}
        </p>
      </template>
    </v-alert>
    <!-- Rendered in place and contained, so it covers only the app, never the host page. scroll-strategy
         "none" guarantees it never scroll-locks the host; the default locks any scrolling ancestor it reaches. -->
    <v-overlay
      :model-value="isTransitioning"
      contained
      scroll-strategy="none"
      persistent
      scrim="var(--hes-color-white)"
      opacity="0.6"
      class="align-center justify-center"
      content-class="loadingOverlayContent"
      data-cy="loading-overlay"
    >
      <ProgressSpinner data-cy="loading-overlay-spinner" />
    </v-overlay>

    <component
      :is="component"
      :key="renderCount"
      :assessment-engine="assessmentEngine"
      :report-engine="reportEngine"
      :report-props="reportProps"
      :configuration="configuration"
      :message="message"
      :is-report-blocked="isReportBlocked"
      :is-resuming="isResuming"
      @clicked="displayCurrentState"
      @start-child-assessment="startChildAssessment"
    />
  </v-app>
</template>

<style lang="scss" scoped>
// Half of ProgressSpinner's height (110px plus its 35px top margin).
$loadingSpinnerHalfHeight: 73px;

.v-application {
  // The overlay spans the whole app, which can be taller than the viewport or sit below a host header.
  // Sticky on both edges keeps the spinner at the middle of the viewport while staying inside the app.
  :deep(.loadingOverlayContent) {
    position: sticky;
    top: calc(50vh - #{$loadingSpinnerHalfHeight});
    bottom: calc(50vh - #{$loadingSpinnerHalfHeight});
  }
}

.hes-alert {
  position: fixed;
  top: 10px;
  width: 90%;
  left: 5%;
  z-index: 2500;

  &:hover,
  &:focus {
    cursor: pointer;
  }
}
</style>
